import type { ReactNode } from "react";
import { SkeletonTable } from "./Skeleton";

interface DataTableProps<T> {
  headers: readonly string[];
  data: T[];
  isLoading: boolean;
  emptyMessage: string;
  rowKey: (item: T, index: number) => string | number;
  renderRow: (item: T, index: number) => ReactNode;
  skeletonRows?: number;
  rowClassName?: string;
}

export const DataTable = <T,>({
  headers,
  data,
  isLoading,
  emptyMessage,
  rowKey,
  renderRow,
  skeletonRows = 8,
  rowClassName = "hover:bg-slate-50",
}: DataTableProps<T>) => (
  <div className="overflow-hidden rounded-lg bg-white shadow-card">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-ink-secondary">
          <tr>
            {headers.map((h, i) => (
              <th
                key={h}
                scope="col"
                className={`px-4 py-3${i === headers.length - 1 ? " text-right" : ""}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {isLoading ? (
            <tr>
              <td colSpan={headers.length} className="px-4 py-4">
                <SkeletonTable rows={skeletonRows} cols={headers.length} />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={headers.length}
                className="px-4 py-6 text-center text-ink-secondary"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr key={rowKey(item, index)} className={rowClassName}>
                {renderRow(item, index)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);